import { Slot } from '@radix-ui/react-slot';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './Badge.module.scss';

/**
 * Начертание метки — насколько громко она звучит рядом с содержимым.
 *
 * `soft` — по умолчанию: заметно, но не спорит с текстом строки.
 * `outline` — самый тихий вариант для плотных таблиц.
 * `solid` — когда метка важнее самой строки; на экране таких единицы.
 */
export type BadgeVariant = 'soft' | 'outline' | 'solid';

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

export type BadgeSize = 'sm' | 'md';

const VARIANT: Record<BadgeVariant, string> = {
  soft: styles['badge--soft'] ?? '',
  outline: styles['badge--outline'] ?? '',
  solid: styles['badge--solid'] ?? '',
};

const TONE: Record<BadgeTone, string> = {
  neutral: styles['badge--neutral'] ?? '',
  accent: styles['badge--accent'] ?? '',
  success: styles['badge--success'] ?? '',
  warning: styles['badge--warning'] ?? '',
  danger: styles['badge--danger'] ?? '',
};

const SIZE: Record<BadgeSize, string> = {
  sm: styles['badge--sm'] ?? '',
  md: styles['badge--md'] ?? '',
};

export interface BadgeProps extends Omit<ComponentPropsWithRef<'span'>, 'color'> {
  variant?: BadgeVariant;
  tone?: BadgeTone;
  size?: BadgeSize;
  /** Иконка перед подписью. */
  icon?: ReactNode;
  /** Моноширинное начертание — для ключей, слагов и идентификаторов. */
  mono?: boolean;
  /** Отрисовать переданный элемент вместо span: ссылку или кнопку-фильтр. */
  asChild?: boolean;
  children: ReactNode;
}

/**
 * Метка категории: тариф, роль, тег.
 *
 * Отвечает на вопрос «чем это является», а не «что с этим происходит».
 * Для состояний есть отдельный компонент `Status` — если помечать состояние
 * меткой, тариф `pro` и состояние «опубликовано» получат одинаковый вид, и
 * глаз перестанет отличать одно от другого.
 */
export function Badge({
  variant = 'soft',
  tone = 'neutral',
  size = 'md',
  icon,
  mono = false,
  asChild = false,
  className,
  children,
  ...rest
}: BadgeProps) {
  const Component = asChild ? Slot : 'span';

  return (
    <Component
      className={cx(
        styles['badge'],
        VARIANT[variant],
        TONE[tone],
        SIZE[size],
        mono && styles['badge--mono'],
        className,
      )}
      {...rest}
    >
      {asChild ? (
        children
      ) : (
        <>
          {icon !== undefined && icon !== null && (
            <span className={styles['badge__icon']} aria-hidden="true">
              {icon}
            </span>
          )}
          {children}
        </>
      )}
    </Component>
  );
}
